import { type HeadersFunction, json, type LoaderFunctionArgs, MetaFunction } from '@remix-run/node';
import { useLoaderData } from '@remix-run/react';

import Homepage from '~/sections/homepage/Homepage';
import {
  CategoryHighlightsSettings,
  HomepageSettings,
  ProductsSliderSettings,
  PromotionalBanner,
  RawCategory,
  RawCategoryHighlightsData,
  RawPromotionalBannersData,
  RawReviewsData,
  RawSliderData,
  RawTile,
} from '~/types/Homepage';
import { Product } from '~/types/Product';
import { Review } from '~/types/Review';
import { apiFetch } from '~/utils/apiFetch';
import { publicPageHeaders } from '~/utils/cacheControl.server';
import { buildSeoMeta, getHomepageSeo, getRootOrigin, getShopName } from '~/utils/meta';
import { resolveRequestChannel } from '~/utils/requestChannel.server';
import { cachedFetch } from '~/utils/serverCache.server';

interface Props {
  settings?: HomepageSettings;
  apiUrl?: string;
}

export async function loader({ request }: LoaderFunctionArgs) {
  const API_URL = process.env.PUBLIC_API_URL;

  // Shared, non-personal settings — cached without forwarding Cookie/Authorization. The
  // channel/locale are resolved fresh for THIS request (never from process-global state),
  // so a cookie-less visitor can never seed a cache entry under the wrong channel.
  const { locale, channelCode } = await resolveRequestChannel(request);
  const settingsHeaders = { 'Accept-Language': locale, 'Accept-Channel': channelCode };

  const [
    mainBanners,
    productsSlider1Res,
    categoryHighlights,
    informationBlock,
    productsSlider2Res,
    promotionalBanners,
    reviewsRes,
  ] = await Promise.all([
    cachedFetch(`${API_URL}/api/v2/shop/settings/app.homepage_main_banners`, { headers: settingsHeaders, ttl: 60 }),
    cachedFetch(`${API_URL}/api/v2/shop/settings/app.homepage_carousel_1`, { headers: settingsHeaders, ttl: 60 }),
    cachedFetch(`${API_URL}/api/v2/shop/settings/app.homepage_category_highlights`, {
      headers: settingsHeaders,
      ttl: 60,
    }),
    cachedFetch(`${API_URL}/api/v2/shop/settings/app.homepage_information_block`, {
      headers: settingsHeaders,
      ttl: 60,
    }),
    cachedFetch(`${API_URL}/api/v2/shop/settings/app.homepage_carousel_2`, { headers: settingsHeaders, ttl: 60 }),
    cachedFetch(`${API_URL}/api/v2/shop/settings/app.homepage_secondary_banners`, {
      headers: settingsHeaders,
      ttl: 60,
    }),
    cachedFetch(`${API_URL}/api/v2/shop/testimonials`, {
      headers: { ...settingsHeaders, Accept: 'application/json' },
      ttl: 60,
    }),
  ]);

  const productsSlider1Data = productsSlider1Res.ok ? await productsSlider1Res.json() : null;
  const productsSlider2Data = productsSlider2Res.ok ? await productsSlider2Res.json() : null;

  // Helper to group banner_* properties into a banner object
  const formatSliderData = (data: RawSliderData | null, products: Product[]): ProductsSliderSettings | null => {
    if (!data?.settings) return null;
    const { settings } = data;
    return {
      title: settings.carousel_title || '',
      subtitle: settings.carousel_subtitle || '',
      banner: {
        title: settings.banner_title || '',
        subtitle: settings.banner_subtitle || '',
        link: settings.banner_link || '',
        buttonTitle: settings.banner_button_title || '',
        image: settings.banner_image ?? '',
      },
      products,
    };
  };

  // Helper to format categoryHighlights with full image URLs
  const formatCategoryHighlights = (data: RawCategoryHighlightsData): CategoryHighlightsSettings | null => {
    if (!data?.settings) return null;
    const { settings } = data;
    return {
      settings: {
        tiles:
          settings.tiles?.map((tile: RawTile) => ({
            ...tile,
            image: tile.image ?? '',
          })) || [],
        categories:
          settings.categories?.map((category: RawCategory) => ({
            ...category,
            image: category.image ?? '',
          })) || [],
      },
    };
  };

  // Helper to format promotionalBanners (backend already drops banners missing a title)
  const formatPromotionalBanners = (data: RawPromotionalBannersData): PromotionalBanner[] | null => {
    if (!data?.settings?.length) return null;

    return data.settings.map((banner) => ({
      desktopImage: banner.desktopImage ?? '',
      mobileImage: banner.mobileImage ?? '',
      desktopImageSet: banner.desktopImageSet,
      mobileImageSet: banner.mobileImageSet,
      link: banner.link || '',
      title: banner.title || '',
      subtitle: banner.subtitle || '',
      buttonTitle: banner.buttonTitle || '',
    }));
  };

  const formatReviews = (data: RawReviewsData | null): Review[] => {
    const list = Array.isArray(data) ? data : (data?.member ?? data?.['hydra:member'] ?? []);
    return list.flatMap((review) => {
      const author = review.author?.trim();
      const text = review.text?.trim();
      if (!author || !text) return [];
      return [{ author, text, rating: Math.min(5, Math.max(1, Math.round(Number(review.rating) || 5))) }];
    });
  };

  // Fetch products for each slider based on category setting (same endpoint as category pages)
  const [slider1ProductsRes, slider2ProductsRes] = await Promise.all([
    productsSlider1Data?.settings?.category
      ? apiFetch(
          `${API_URL}/api/v2/shop/products/search?limit=10&facets[taxon][]=${productsSlider1Data.settings.category}&imageFilter=sylius_shop_product_thumbnail`,
          {},
          { request }
        )
      : Promise.resolve(null),
    productsSlider2Data?.settings?.category
      ? apiFetch(
          `${API_URL}/api/v2/shop/products/search?limit=10&facets[taxon][]=${productsSlider2Data.settings.category}&imageFilter=sylius_shop_product_thumbnail`,
          {},
          { request }
        )
      : Promise.resolve(null),
  ]);

  const slider1Products = slider1ProductsRes?.ok ? (await slider1ProductsRes.json()).products || [] : [];
  const slider2Products = slider2ProductsRes?.ok ? (await slider2ProductsRes.json()).products || [] : [];

  const settings = {
    mainBanners: mainBanners.ok ? await mainBanners.json() : null,
    productsSlider1: formatSliderData(productsSlider1Data, slider1Products),
    categoryHighlights: categoryHighlights.ok ? formatCategoryHighlights(await categoryHighlights.json()) : null,
    informationBlock: informationBlock.ok ? await informationBlock.json() : null,
    productsSlider2: formatSliderData(productsSlider2Data, slider2Products),
    promotionalBanners: promotionalBanners.ok ? formatPromotionalBanners(await promotionalBanners.json()) : null,
    reviews: reviewsRes.ok ? formatReviews(await reviewsRes.json()) : [],
  };

  return json({
    settings,
    apiUrl: API_URL,
  });
}

export const headers: HeadersFunction = ({ parentHeaders }) => publicPageHeaders(parentHeaders);

export const meta: MetaFunction<typeof loader> = ({ matches, location }) => {
  return buildSeoMeta({
    seo: getHomepageSeo(matches),
    fallbackTitle: 'Sauny a saunové doplňky',
    fallbackDescription:
      'Nabízíme kvalitní sauny, venkovní sauny a sauny na míru pro váš maximální relax. Vše pro dokonalé saunování, včetně kvalitních doplňků.',
    origin: getRootOrigin(matches),
    shopName: getShopName(matches),
    path: location.pathname,
  });
};

export default function HomepageRoute() {
  const data = useLoaderData<typeof loader>() as Props;

  // No <link rel=preload> for the hero image: Lighthouse pulls a high-priority preloaded
  // image into its (simulated-throttling) FCP dependency graph, which pushed the mobile FCP
  // score from ~1.58s to 2.7-3.9s despite no change in real/observed FCP. `fetchpriority="high"`
  // + `loading="eager"` on the first tile's own <img> (see Homepage.tsx) still gets it
  // discovered and fetched early, just without that scoring penalty.
  return <Homepage {...data} />;
}
